import React from 'react';
import { TrendingUp, Timer, ChevronDown, ChevronUp } from 'lucide-react';

const COUNT_IN_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 4, label: '4' },
];

export default function PracticePanel({
  // Count-in
  countInBars,
  onCountInChange,
  // Ramp
  rampEnabled,
  rampDelta,
  rampEveryBars,
  rampMaxBpm,
  onRampToggle,
  onRampDeltaChange,
  onRampEveryBarsChange,
  onRampMaxBpmChange,
  // Live
  effectiveBpm,
  baseBpm,
  isPlaying,
  collapsed,
  onCollapseToggle,
}) {
  const ramping = isPlaying && rampEnabled && effectiveBpm > baseBpm;

  return (
    <section
      data-testid="practice-panel"
      className="bg-[#111112] border border-white/5 rounded-2xl shadow-xl overflow-hidden"
    >
      <button
        data-testid="practice-panel-toggle"
        onClick={onCollapseToggle}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3">
          <Timer className="w-4 h-4 text-pink-400" />
          <div className="text-left">
            <div className="text-xs uppercase tracking-[0.25em] text-white/50">
              Studio Tools
            </div>
            <div className="font-bebas text-xl tracking-wide">
              Count-in {countInBars > 0 ? `· ${countInBars}` : '· off'}
              <span className="mx-2 text-white/20">·</span>
              Ramp {rampEnabled ? `+${rampDelta}/${rampEveryBars}b` : 'off'}
              {ramping && (
                <span className="ml-3 text-pink-300 text-base tabular-nums">
                  → {effectiveBpm} bpm
                </span>
              )}
            </div>
          </div>
        </div>
        {collapsed ? <ChevronDown className="w-5 h-5 text-white/40" /> : <ChevronUp className="w-5 h-5 text-white/40" />}
      </button>

      {!collapsed && (
        <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-white/5 pt-5">
          {/* Count-in */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Timer className="w-4 h-4 text-white/60" />
              <h3 className="font-bebas text-lg tracking-wide">Count-in</h3>
            </div>
            <p className="text-xs text-white/50 mb-3 leading-relaxed">
              Battute di click prima di iniziare la riproduzione, sul tempo corrente.
            </p>
            <div
              data-testid="count-in-options"
              role="radiogroup"
              aria-label="Count-in"
              className="flex gap-2 flex-wrap"
            >
              {COUNT_IN_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  data-testid={`count-in-${opt.value}-btn`}
                  role="radio"
                  aria-checked={countInBars === opt.value}
                  onClick={() => onCountInChange(opt.value)}
                  className={`h-10 px-4 rounded-md border font-bebas text-lg tracking-wide transition-all ${
                    countInBars === opt.value
                      ? 'border-pink-400 bg-pink-500/15 text-pink-200 shadow-[0_0_14px_rgba(236,72,153,0.25)]'
                      : 'border-white/10 hover:border-white/30 text-white/70'
                  }`}
                >
                  {opt.label}
                  {opt.value > 0 && <span className="text-xs text-white/50 ml-1.5">bar</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Practice ramp */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-white/60" />
                <h3 className="font-bebas text-lg tracking-wide">Practice Ramp</h3>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="ramp-toggle"
                  type="checkbox"
                  checked={rampEnabled}
                  onChange={(e) => onRampToggle(e.target.checked)}
                  className="accent-pink-400 w-4 h-4"
                />
                <span className="text-xs uppercase tracking-widest text-white/60">
                  {rampEnabled ? 'Attiva' : 'Disattiva'}
                </span>
              </label>
            </div>
            <p className="text-xs text-white/50 mb-3 leading-relaxed">
              Aumenta automaticamente il BPM ogni N battute fino al limite. Perfetto per costruire velocità.
            </p>
            <div className={`grid grid-cols-3 gap-2 ${rampEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-white/50">+ BPM</span>
                <input
                  data-testid="ramp-delta-input"
                  type="number"
                  min={1}
                  max={20}
                  value={rampDelta}
                  onChange={(e) => onRampDeltaChange(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
                  className="h-10 bg-[#0A0A0A] border border-white/10 rounded-md px-2 font-bebas text-xl text-center tabular-nums"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-white/50">ogni · bar</span>
                <input
                  data-testid="ramp-every-input"
                  type="number"
                  min={1}
                  max={64}
                  value={rampEveryBars}
                  onChange={(e) => onRampEveryBarsChange(Math.max(1, Math.min(64, parseInt(e.target.value, 10) || 1)))}
                  className="h-10 bg-[#0A0A0A] border border-white/10 rounded-md px-2 font-bebas text-xl text-center tabular-nums"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-white/50">max bpm</span>
                <input
                  data-testid="ramp-max-input"
                  type="number"
                  min={30}
                  max={300}
                  value={rampMaxBpm}
                  onChange={(e) => onRampMaxBpmChange(Math.max(30, Math.min(300, parseInt(e.target.value, 10) || 300)))}
                  className="h-10 bg-[#0A0A0A] border border-white/10 rounded-md px-2 font-bebas text-xl text-center tabular-nums"
                />
              </label>
            </div>
            {ramping && (
              <div data-testid="ramp-live" className="mt-3 text-xs text-pink-300 font-mono uppercase tracking-widest">
                ▲ Ramping: {baseBpm} → <span className="text-base font-bebas tracking-wide">{effectiveBpm}</span> bpm
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
